// Porta de reference/expo-frontend/lib/roundStatus.ts + livePicks.ts —
// helpers puros das seções "Meus palpites" e "Placar ao vivo" da Home.

export type LiveEntry = {
  position: number | null;
  playerId: string;
  playerName: string;
  totalScore?: number | null;
  thru?: number | null;
  teeTime?: string | null;
};

export type RoundStatus = { text: string; flag: boolean } | null;

/**
 * Status da rodada de um jogador: "F" quando completou a volta (thru >= 18),
 * o buraco atual com ícone de bandeira enquanto está em campo, ou o horário
 * de saída antes de começar. Tee times são wall-clock do torneio pinados em
 * UTC pelo servidor — SEMPRE formatar com getters UTC (gotcha herdado).
 */
export function formatRoundStatus(entry: {
  thru?: number | null;
  teeTime?: string | null;
}): RoundStatus {
  const thru = entry.thru ?? null;
  if (thru !== null && thru >= 18) return { text: "F", flag: false };
  if (thru !== null && thru > 0) return { text: `${thru}`, flag: true };
  if (entry.teeTime) {
    const d = new Date(entry.teeTime);
    if (!Number.isNaN(d.getTime())) {
      const hh = String(d.getUTCHours()).padStart(2, "0");
      const mm = String(d.getUTCMinutes()).padStart(2, "0");
      return { text: `${hh}:${mm}`, flag: false };
    }
  }
  return null;
}

export function formatScore(score: number | null | undefined): string {
  if (score === null || score === undefined) return "—";
  if (score === 0) return "E";
  return score > 0 ? `+${score}` : `${score}`;
}

/** Preferência do usuário (app original): "T" em todas as colocações,
 *  mesmo sem empate. */
export function formatPosition(position: number | null): string {
  return position == null ? "" : `T${position}`;
}

export type MyBetPicksLike = {
  playerIds: string[];
  // Nome por pick, alinhado com playerIds (pode faltar em apostas antigas).
  playerNames?: (string | null)[];
};

// Deduplica os picks do usuário preservando o primeiro nome de snapshot
// não-vazio encontrado para cada jogador.
export function collectMyPickSnapshots(
  bets: MyBetPicksLike[],
): Map<string, string | undefined> {
  const byId = new Map<string, string | undefined>();
  for (const bet of bets) {
    bet.playerIds.forEach((pid, i) => {
      const snap = bet.playerNames?.[i] ?? undefined;
      const existing = byId.get(pid);
      if (!byId.has(pid) || (!existing && snap)) {
        byId.set(pid, snap || undefined);
      }
    });
  }
  return byId;
}

export type MyLivePick = {
  pid: string;
  snapshotName: string | undefined;
  // Entrada ao vivo no leaderboard; ausente = jogador fora do feed (o Data
  // Golf omite quem perdeu o corte, então com leaderboard carregado isso
  // significa CUT). Posição null em entrada explícita também indica CUT/WD/DQ.
  entry: LiveEntry | undefined;
};

// Picks do usuário que NÃO estão no Top exibido (esses são destacados na
// própria linha, sem duplicar). Ordena por posição real no campo; picks sem
// entrada ou posição null (CUT) vão para o final.
export function myPicksOutsideTop(
  pickSnapshotById: Map<string, string | undefined>,
  leaderboard: LiveEntry[],
  topLeaderboard: LiveEntry[],
): MyLivePick[] {
  const topIds = new Set(topLeaderboard.map((e) => e.playerId));
  return [...pickSnapshotById.entries()]
    .filter(([pid]) => !topIds.has(pid))
    .map(([pid, snapshotName]) => ({
      pid,
      snapshotName,
      entry: leaderboard.find((e) => e.playerId === pid),
    }))
    .sort(
      (a, b) =>
        (a.entry?.position ?? Infinity) - (b.entry?.position ?? Infinity),
    );
}

export type TournamentBetLike = {
  betId: string;
  userId: string;
  userName: string;
  playerIds: string[];
  // Nome por pick, alinhado com playerIds (null = desconhecido no servidor).
  playerNames?: (string | null)[];
  createdAt: string;
};

export type FrontRunnerEntry = TournamentBetLike & {
  hits: number;
  bestPosition: number | null;
};

// Porta de computeFrontRunners do app original (lib/matchPlayer.ts):
// ranqueia os palpites do torneio por quantos dos 3 picks estão no Top 3
// ao vivo ("quem está na frente"). Empate decidido pela melhor (menor)
// posição entre os picks que acertaram; palpites sem acerto mantêm a ordem
// de criação. Única adaptação web: match direto por playerId — o servidor
// já resolve IDs legados antes de gravar, então apostas e leaderboard usam
// os mesmos provider IDs (sem roster local nem matching por nome).
export function computeFrontRunners(
  leaderboard: LiveEntry[],
  bets: TournamentBetLike[],
  limit = 5,
): FrontRunnerEntry[] {
  const top3 = leaderboard.filter((e) => e.position !== null).slice(0, 3);
  if (top3.length < 3 || bets.length === 0) return [];

  const positionByPlayerId = new Map<string, number>();
  top3.forEach((entry, idx) => {
    if (!positionByPlayerId.has(entry.playerId)) {
      positionByPlayerId.set(entry.playerId, idx + 1);
    }
  });

  const scored: FrontRunnerEntry[] = bets.map((bet) => {
    const hitPositions = bet.playerIds
      .map((pid) => positionByPlayerId.get(pid))
      .filter((p): p is number => p !== undefined);
    return {
      ...bet,
      hits: hitPositions.length,
      bestPosition: hitPositions.length > 0 ? Math.min(...hitPositions) : null,
    };
  });

  scored.sort((a, b) => {
    if (b.hits !== a.hits) return b.hits - a.hits;
    if (a.hits === 0) {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    }
    const aPos = a.bestPosition ?? Infinity;
    const bPos = b.bestPosition ?? Infinity;
    if (aPos !== bPos) return aPos - bPos;
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });

  return scored.slice(0, limit);
}

// Destaque âmbar por jogador: os 3 primeiros playerIds do leaderboard
// (mesmos provider IDs das apostas — sem resolução de nome). Evita o check
// position <= 3, que super-destaca em caso de empates.
export function liveTop3Ids(leaderboard: LiveEntry[]): Set<string> {
  return new Set(
    leaderboard
      .filter((e) => e.position !== null)
      .slice(0, 3)
      .map((e) => e.playerId),
  );
}
